<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="radio-button-set-main">
    <!-- BoxContainer doesn't support raw html in the title, which is needed to set the text colour of the required indicator -->
    <!-- draw own div instead of using BoxContainer -->
    <div v-if="showBox" class="radio-buttons-with-box">
      <div class="title">
        <span v-if="isRequired" class="is-required-indicator">**</span>
        <span>{{title}}</span>
      </div>
      <div :class="radioButtonsContainerClasses" id="boxed-radio-buttons" @focusin="radioButtonsContainerFocusedIn" @click="radioButtonsContainerClicked">
        <RadioButtonV2 v-for="(button, buttonIndex) in buttons" :key="buttonIndex" :label="button[labelKey]" :name="radioButtonName" :isDisabled="isDisabled || button['isDisabled']" @radioChanged="radioChanged" :ref="button[labelKey]"/>
        <div v-if="errorMessage" class="error-message-container" :style="errorMessageContainerStyling">
          <span>{{errorMessage}}</span>
        </div>
      </div>
    </div>
    <div v-else class="radio-buttons-without-box">
      <div class="title">
        <span v-if="isRequired" class="is-required-indicator">** </span>
        <span>{{title}}</span>
      </div>
      <div :class="radioButtonsContainerClasses" @focusin="radioButtonsContainerFocusedIn" @click="radioButtonsContainerClicked">
        <RadioButtonV2 v-for="(button, buttonIndex) in buttons" :key="buttonIndex" :label="button[labelKey]" :name="radioButtonName" :isDisabled="isDisabled || button['isDisabled']" @radioChanged="radioChanged" :ref="button[labelKey]"/>
        <div v-if="errorMessage" class="error-message-container" :style="errorMessageContainerStyling">
          <span>{{errorMessage}}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {
  },
  computed: {
    radioButtonsContainerClasses: function() {
      return [
        "radio-buttons-container",
        this.isHorizontal ? "row-wise" : "column-wise",
      ];
    },
    radioButtonName: function() { // generate a unique name if one isn't passed in
      return this.name || `${this.title}_${new Date().formatString("yyyyMMddHHmmss_sss")}`; // use the current timestamp to differentiate between two radio button sets with the same title
    },
    errorMessageContainerStyling: function() {
      if (this.isHorizontal) {
        return { // only works if check boxes are lined up horizontally
          "text-overflow": "ellipsis", // because text overflow ellipsis needs white space nowrap to work https://developer.mozilla.org/en-US/docs/Web/CSS/text-overflow
          "white-space": "nowrap", // but white space nowrap forces the error message into a single line, you can't have ellipsis with multiple lines
        };
      } else { // webkit line clamp is needed if the ellipsis needs to be shown not on the first line, but at the end after a subsequent number of lines
        return { // https://stackoverflow.com/a/17029025 https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp https://css-tricks.com/almanac/properties/l/line-clamp/
          "display": "-webkit-box", // webkit line clamp is supported across all major browsers, but as of writing this line clamp is being defined in the CSS standard
          "-webkit-box-orient": "vertical", // which will replace webkit line clamp in the future
          "-webkit-line-clamp": this.buttons?.length || "1", // the number of lines needs to be specified, and needs to be greater than 0
        };
      }
    },
  },
  data: function() {
    return {
    };
  },
  props: {
    title: { type: String, default: 'Radio Button Title' }, // The title of the radio button set

    buttons: { type: Array, default: function() { return [] } }, // The array of objects that will be shown at the check boxes
    // format: [
    //   {
    //     labelKey: "Radio Button 1", // the key to be displayed as the button text should be passed to the labelKey prop
    //   },
    //   {
    //     labelKey: "Radio Button 2",
    //   },
    // ]
    labelKey: { type: String, default: '' }, // The key of the object to show as the radio button label
    name: { type: String, default: '' }, // OPTIONAL: used to define different groups of radio buttons, so the force only one radio button to be selected per group
    selected: { type: Object, default: function() { return null; } }, // selected button, set to null to deselect all radio buttons, use .sync
    isHorizontal: { type: Boolean, default: false }, // If true, the radio buttons will appear in a row
    showBox: { type: Boolean, default: true }, // If false, the box around the radio buttons will be removed
    isRequired: { type: Boolean, default: false }, // If true, user needs to select at least one
    errorMessage: { type: String, default: '' }, // Set an error message, use .sync
    isDisabled: { type: Boolean, default: false }, // To disable all radio buttons and disallow selection

    // Public events
    // @radioChanged - fired when a radio button is selected by the user, but not when changed programatically
  },
  methods: {
    updateRadioButtonCheckedStates: function() {
      // because ref was used on a component with v-for, it returns an array that contains the component instance instead
      // thus the need to access the first element after specifying the ref

      for (const button of this.buttons) {
        this.$refs[button[this.labelKey]][0].clearRadio(); // unselect all radio buttons
      }

      if (!this.selected) { // if selected was cleared
        return;
      }

      const buttonIndex = this.buttons.findIndex((button) => { // find and select the specified radio button
        return button[this.labelKey] === this.selected[this.labelKey];
      });
      const buttonRef = this.buttons[buttonIndex][this.labelKey];
      this.$refs[buttonRef][0].selectRadio();
    },
    radioChanged: function(label, event) {
      const selectedButton = this.buttons.find((button) => {
        return button[this.labelKey] === label;
      });
      this.$emit("update:selected", selectedButton);
      this.$emit("radioChanged", selectedButton);
    },
    radioButtonsContainerFocusedIn: function(focusEvent) { // clear error message if any of the buttons have been focused on, including if it was focused on via keyboard
      this.$emit("update:errorMessage", ""); // specifically, listen to focusin instead of focus, because focusin bubbles up from the input to the div
    },
    radioButtonsContainerClicked: function(pointerEvent) { // clear error message if the container has been clicked on
      this.$emit("update:errorMessage", ""); // the container may be larger than the buttons, and it's possible for the user to click on the container but not any of the buttons
    },

    // Public Methods
    setFocus: function() { // focuses on the first radio button in the array
      this.$refs[this.buttons[0][this.labelKey]][0].setFocus();
      // because ref was used on a component with v-for, it returns an array that contains the component instance instead
      // thus the need to access the first element after specifying the ref

      this.$emit("update:errorMessage", "");
    },
  },
  mounted() {
    if (!this.buttons || this.buttons.length === 0) {
      if (process.env.NODE_ENV === "development") {
        console.error("RadioButtonSet - Buttons not provided");
      }
      return;
    }

    if (this.labelKey === "") {
      if (process.env.NODE_ENV === "development") {
        console.error("RadioButtonSet - labelKey not provided");
      }
      return;
    }

    if (this.selected) {
      this.updateRadioButtonCheckedStates();
    }
  },
  watch: {
    selected: {
      handler: function(val) {
        this.updateRadioButtonCheckedStates();
      },
      deep: true,
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.radio-button-set-main {

  > .radio-buttons-with-box {
    position: relative;
    border: 1px solid gray;
    margin-top: 8px;
    padding: 10px 5px 5px 5px;
    border-radius: 5px;

    > .title {
      position: absolute;
      top: -9px;
      left: 10px;
      padding: 0 10px;
      background-color: white;

      > .is-required-indicator {
        margin: 0 5px 0 -5px;
        color: red;
      }
    }
  }

  > .radio-buttons-without-box {
    display: flex;
    flex-direction: column;

    .is-required-indicator {
      color: red;
    }
  }

  .radio-buttons-container {
    display: flex;

    > .error-message-container {
      position: absolute;
      border-radius: 5px;
      padding: 1px 5px;
      max-height: calc(100% - 15px); // this doesn't work properly if no box is shown
      max-width: calc(100% - 10px); // the error message will grow past the component's boundaries
      background-color: red; // couldn't find a way to fix it, either show the box or use a shorter message
      color: white;
      overflow: hidden;
    }

    &:hover > .error-message-container {
      border: 1px solid black;
      max-height: unset;
      max-width: unset;
      -webkit-line-clamp: unset !important; // used when check boxes are aligned vertically
    }
  }

  .row-wise {
    flex-direction: row;
  }

  .column-wise {
    flex-direction: column;
  }

  #boxed-radio-buttons { // offset by 2px from the top
    margin-top: 2px; // to prevent white background of the title from overlapping with the radio buttons
  }
}

</style>